import { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../../context/authContext';
// Added Eye and EyeOff to the imports
import { User, Lock, Eye, EyeOff } from 'lucide-react'; 

export default function Login() {
  const [formData, setFormData] = useState({
    email: localStorage.getItem('rememberedEmail') || '',
    password: '',
  });

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // --- ADDED: PASSWORD VISIBILITY STATE ---
  const [showPassword, setShowPassword] = useState(false);
  
  // --- CAROUSEL STATE ---
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const { login, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const slides = [
    {
      title: "Welcome \nBack!",
      text: "Connect with your university community, track your academics, and stay updated with OpenSocialM."
    },
    {
      title: "Join the \nConversation",
      text: "Participate in faculty discussions, share ideas, and collaborate with peers in real-time."
    },
    {
      title: "Never Miss \nan Update",
      text: "Get instant notifications about exams, events, and important announcements directly on your dashboard."
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); 
    return () => clearInterval(interval);
  }, [slides.length]);

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    localStorage.setItem('rememberedEmail', formData.email);

    try {
      const success = await login(formData);
      if (success) {
        navigate('/dashboard', { replace: true });
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Login failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#8CABFF] dark:bg-slate-900 transition-colors duration-300 flex items-center justify-center p-4 relative font-['Lato']">
      
      <div className="bg-white dark:bg-slate-800 rounded-[30px] shadow-2xl overflow-hidden flex w-full max-w-[1000px] min-h-[600px] transition-colors duration-300">
        
        <div className="hidden md:flex w-1/2 bg-gradient-to-br from-[#1A237E] to-[#3949AB] dark:from-indigo-900 dark:to-slate-900 items-center justify-center relative p-12 text-white overflow-hidden transition-all duration-500">
          <div className="relative z-10 w-full animate-fade-in-up">
            <h1 className="text-5xl font-bold font-['Playfair_Display'] mb-6 leading-tight whitespace-pre-line transition-all duration-500 tracking-wide">
              {slides[currentSlide].title}
            </h1>
            <p className="text-blue-100 text-lg leading-relaxed opacity-90 min-h-[80px] transition-all duration-500 font-light">
              {slides[currentSlide].text}
            </p>
            
            <div className="mt-12 flex gap-3">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? 'w-8 bg-white opacity-100' 
                      : 'w-2 bg-white opacity-40 hover:opacity-70'
                  }`}
                />
              ))}
            </div>
          </div>
          <div className="absolute top-[-50px] left-[-50px] w-40 h-40 rounded-full bg-white opacity-5 animate-pulse"></div>
          <div className="absolute bottom-[-20px] right-[-20px] w-60 h-60 rounded-full bg-white opacity-5"></div>
        </div>

        <div className="w-full md:w-1/2 bg-white dark:bg-slate-800 p-8 md:p-12 flex flex-col justify-center transition-colors duration-300">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold font-['Playfair_Display'] text-[#1A237E] dark:text-white mb-2 tracking-tight">
              Welcome Back
            </h2>
            <p className="text-gray-400 text-sm font-medium tracking-wide">
              Please enter your details to sign in.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-300 text-sm text-center border border-red-100 dark:border-red-800">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400 group-focus-within:text-[#1A237E] transition-colors" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="Username or Email"
                value={formData.email}
                onChange={handleChange}
                className="w-full pl-11 pr-4 py-4 bg-gray-50 dark:bg-slate-700 border border-gray-100 dark:border-slate-600 text-gray-900 dark:text-white text-sm rounded-xl focus:ring-2 focus:ring-[#1A237E] focus:border-transparent outline-none transition-all placeholder-gray-400 dark:placeholder-gray-500 font-medium tracking-wide"
              />
            </div>

            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-[#1A237E] transition-colors" />
              </div>
              <input
                id="password"
                name="password"
                // Logic updated: toggle between password and text
                type={showPassword ? "text" : "password"}
                required
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                className="w-full pl-11 pr-12 py-4 bg-gray-50 dark:bg-slate-700 border border-gray-100 dark:border-slate-600 text-gray-900 dark:text-white text-sm rounded-xl focus:ring-2 focus:ring-[#1A237E] focus:border-transparent outline-none transition-all placeholder-gray-400 dark:placeholder-gray-500 font-medium tracking-wide"
              />
              
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-[#1A237E] dark:hover:text-blue-400 transition-colors focus:outline-none"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>

            <div className="text-left">
              <Link 
                to="/forgot-password" 
                className="text-xs font-bold text-gray-500 dark:text-gray-400 hover:text-[#1A237E] dark:hover:text-blue-400 transition-colors uppercase tracking-wider"
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#1A237E] hover:bg-[#151b60] text-white font-bold py-4 rounded-xl shadow-lg shadow-indigo-500/20 transition-all transform hover:-translate-y-0.5 active:scale-95 tracking-wider text-sm"
            >
              {isLoading ? 'SIGNING IN...' : 'LOGIN'}
            </button>
          </form>

          <div className="mt-8 text-center text-xs text-gray-500">
            Don't have an account? <Link to="/register" className="font-bold text-[#1A237E] dark:text-blue-400 hover:underline tracking-wide">CREATE ACCOUNT</Link>
          </div>

        </div>
      </div>
    </div>
  );
}